import { useEffect } from 'react'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'

import { BogoBanner, BogoPromoModal } from '@mc/bogo'
import { StaticDataProvider } from '@mc/static-data-react'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { useParsePromoFeatureVariables } from '@mc/promo-utils'
import { EnterpriseAnnouncementBanner } from '@mc/enterprise-announcement'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { TRACK_PROPS, trackPage } from '@mc/track-event'
import { getBucket } from '@mc/next-growthbook'

import { trackProductViewed } from 'libs/events'
import {
  getMarketingRows,
  getGuestPassByReferralToken,
  getCourseSamples,
  getDefaultProduct,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { useSprig } from 'libs/sprig'
import {
  CATEGORIES_TYPE,
  COURSES_TYPE,
  GUEST_PASS_TYPE,
  MARKETING_ROWS_DATA_TYPE,
  PRODUCT_PLAN_TYPE,
} from 'types'
import ProfitWell from 'components/Head/ProfitWell'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { GoogleOneTap } from 'experiments/pen-3951_v2_google_email_capture/components/GoogleOneTap'
import {
  CAROUSEL_SPRINGBOARD_EXP_FLAG,
  getCarouselSpringboardItems,
  useTrackCarouselSpringboardExperiment,
} from 'experiments/pen-5453_v1_hp_carousel_to_quiz'
import { useTrackPen5038ExperimentViewed } from 'experiments/pen-5038_v1_mega_content_picker'
import { useTrackHPTestimonialsExperimentViewed } from 'experiments/pen-5386_v1-hp-testimonials'

import Meta from './index/components/Meta'
import { HeroQuiz } from './index/components/HeroQuiz'

const BelowTheFold = dynamic(() => import('./index/components/BelowTheFold'), {
  ssr: false,
})

const Home = ({
  annualPass,
  categories,
  guestPass,
  marketingRows,
  sampleCourses,
  ctaNextPage,
  carouselSpringboardItems,
}) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewed(annualPass)
    }
  }, [annualPass])

  useEffect(() => {
    trackPage('Home', {
      saved_completed_quiz: !!ctaNextPage,
      platform: 'next',
    })
  }, [ctaNextPage])

  useParsePromoFeatureVariables()
  useTrackPen5038ExperimentViewed()
  useSprig()
  useTrackHPTestimonialsExperimentViewed()
  useTrackCarouselSpringboardExperiment()

  return (
    <StaticDataProvider
      value={{
        annualPass,
        categories,
        guestPass,
        marketingRows,
        sampleCourses,
        ctaNextPage,
        carouselSpringboardItems,
      }}
    >
      <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-home' }}>
        <PromoExplainerModal pageSource={PAGE_SOURCES.HOMEPAGE} />
        <BogoPromoModal />
        <ProfitWell />
        <Meta />
        <BogoBanner className='mc-mb-2 mc-mb-md-0' />
        <EnterpriseAnnouncementBanner />
        <HeroQuiz />
        <GoogleOneTap />
        <BelowTheFold />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Home.propTypes = {
  annualPass: PRODUCT_PLAN_TYPE,
  categories: CATEGORIES_TYPE,
  guestPass: GUEST_PASS_TYPE,
  marketingRows: MARKETING_ROWS_DATA_TYPE,
  sampleCourses: COURSES_TYPE,
  ctaNextPage: PropTypes.string,
  quizSavedFlowClassResultCount: PropTypes.number,
  carouselSpringboardItems: PropTypes.array,
}

Home.pageSettings = {
  title: 'MasterClass - Home',
  showPromo: true,
}

export async function getServerSideProps(ctx) {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  const [ctaNextPage, carouselSpringboardBucket] = await Promise.all([
    getSSRQuizOnlyOnceCookie(ctx),
    getBucket(ctx, CAROUSEL_SPRINGBOARD_EXP_FLAG),
  ])

  const shouldFetchCarouselSpringboardData =
    carouselSpringboardBucket?.startsWith('variant')

  return getPageData({
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      categories: getV2Categories(ctx),
      guestPass: getGuestPassByReferralToken(ctx, { referralToken }),
      marketingRows: getMarketingRows(ctx, {
        include: ['marketing_section_items', 'marketing_section_action'],
      }),
      sampleCourses: getCourseSamples(ctx, { allowAltSet: true }),
      carouselSpringboardItems: shouldFetchCarouselSpringboardData
        ? getCarouselSpringboardItems(ctx)
        : Promise.resolve([]),
    },
    propsMapper: (props) => ({
      ...props,
      ctaNextPage,
    }),
  })
}

export default Home
